import format from "date-fns/format";
import { toDecimal } from "dinero.js";
import sum from "hash-sum";
import memoize from "lodash/memoize";
import { call, resource, sleep } from "starfx";

import { db as workerDb, TWIDBStore } from "@app/stateCommon/idbSchema";
import { convertAmmountToDineroFn } from "@app/statefx/selectors/currency";

import type { TCurrencyRecordSet } from "@app/types/insider";
import type { TCurrency } from "@app/types";
import type { IDBPDatabase } from "idb";
import type { Operation } from "starfx";

export function WorkerStorage(store) {
  if (!store) {
    return;
  }
  const db = workerDb(store);
  return db;
}

const utilsSumAllHasesStartingWith = async (db, prefix) => {
  const tx = db.transaction("hashes", "readonly");
  const store = tx.store;
  const keys = await store.getAllKeys();
  const hashes = [];
  for (const key of keys) {
    if (key.startsWith(prefix)) {
      hashes.push(await store.get(key));
    }
  }
  await tx.done;
  return sum(hashes);
};

const calculateConvValue = (
  e: any,
  currentCurrency: TCurrency,
  targetField: string,
  rates: TCurrencyRecordSet
) => {
  const { MONEDA_FF } = e;
  if (!currentCurrency) return e[targetField];
  const converted = convertAmmountToDineroFn(
    e[targetField],
    MONEDA_FF,
    currentCurrency,
    rates
  );
  return toDecimal(
    converted,
    ({ value, currency }) => `${Number(value).toFixed(2)} ${currency.code}`
  );
};

async function readRates(db, hashValue) {
  const selectedIdxRates = await db.getAll("currencyRates");
  const idxRates = selectedIdxRates.reduce((acc, item) => {
    acc[item.XRDATESTRING] = {
      RON: { amount: Math.floor(Number(item?.RON || 1) * 10000), scale: 4 },
      EUR: { amount: Math.floor(Number(item?.EUR || 0) * 10000), scale: 4 },
      GBP: { amount: Math.floor(Number(item?.GBP || 0) * 10000), scale: 4 },
      USD: { amount: Math.floor(Number(item?.USD || 0) * 10000), scale: 4 },
      CHF: { amount: Math.floor(Number(item?.CHF || 0) * 10000), scale: 4 },
      MDL: { amount: Math.floor(Number(item?.MDL || 1) * 10000), scale: 4 },
    };
    return acc;
  }, {});
  return idxRates;
}

async function getLatestRate(idxRates, hashValue) {
  const latestRates = idxRates[Object.keys(idxRates).pop() || ""] || {};
  return latestRates;
}

async function getIssuersIdx(db: IDBPDatabase<TWIDBStore>, hashValue: string) {
  const cifByAbrTbl = await db.getAll("invoiceIssuer");
  const cifByAbr = cifByAbrTbl.reduce((acc, item) => {
    acc[item.ABR] = item;
    return acc;
  }, {});
  return cifByAbr;
}

// we get this key as the memoizing "reference" for the mapping function
// as long as the rates and the issuers are not changed the mapping function
async function getInvoiceMapperMemoKey(db) {
  const sumHases = await utilsSumAllHasesStartingWith(db, "currencyRates");
  const sumIssuers = await utilsSumAllHasesStartingWith(db, "invoiceIssuer");
  return `${sumHases}:${sumIssuers}`;
}

const readRatesIdxMemo = memoize(readRates, (db, key) => key);
const readLatestRateMemo = memoize(getLatestRate, (idxRates, key) => key);
const getIssuersIdxMemo = memoize(getIssuersIdx, (db, key) => key);

/* 

note:  if at the time of mapping we don't have the latest rates for that day 
 then we will save at the most recent rate available in the database.

 that would be corrected at the next mapping but if is no guarantee of that.

 so. keep in mind some strategy for settle the exrates at a certain point in time.
*/
export function* mapIvFiscalToMain(
  item: any,
  idxRates: { [key: string]: TCurrencyRecordSet } | undefined,
  latestRate: TCurrencyRecordSet,
  issuersByAbrv: { [key: string]: any } | undefined
) {
  const formattedDate = format(new Date(item.LA_DATA), "yyyy-MM-dd");
  const dayRate: TCurrencyRecordSet = idxRates?.[formattedDate] || latestRate;
  const issuerData = issuersByAbrv?.[item.ABR];
  const currencies: TCurrency[] = ["CHF", "EUR", "GBP", "USD", "RON", "MDL"];
  yield* sleep(1);
  for (const currencyCode of currencies) {
    const rateAmount = dayRate?.[currencyCode]?.amount ?? 0;
    item[currencyCode] = rateAmount;

    // Direct assignment to avoid repetitive dynamic property access
    const convValuePrefix = `VAL_${currencyCode}_TXT`;
    const convTvaPrefix = `VAL_TVA_${currencyCode}_TXT`;
    const totalPrefix = `TOTAL_${currencyCode}_TXT`;
    const incasatPrefix = `INCASAT_${currencyCode}_TXT`;
    const restDeIncasatPrefix = `REST_DE_INCASAT_${currencyCode}_TXT`;

    item[convValuePrefix] = calculateConvValue(
      item,
      currencyCode,
      "VAL",
      dayRate
    );
    item[convTvaPrefix] = calculateConvValue(
      item,
      currencyCode,
      "VAL_TVA",
      dayRate
    );
    item[totalPrefix] = calculateConvValue(
      item,
      currencyCode,
      "TOTAL",
      dayRate
    );
    item[incasatPrefix] = calculateConvValue(
      item,
      currencyCode,
      "INCASAT",
      dayRate
    );
    item[restDeIncasatPrefix] = calculateConvValue(
      item,
      currencyCode,
      "REST_DE_INCASAT",
      dayRate
    );
  }

  // Static assignments moved outside of the loop
  const cif = issuerData?.CIF || item.CIF_EMT; // ADDED LATER TO FIX THE SITUATION
  item.OurCIF = cif;
  item.ID = `${cif}/FF_ID${item.FF_ID}`;
  item.SOT_KeyName = "FF_ID";
  item.SOT_KeyValue = item.FF_ID;
  item.SerialNumber = item.NR_FF;
  item.DateIssued = item.LA_DATA;
  item.ContractorName = item.CLIENT;
  item.ContractorCIF = null; // Set to null if not conditional
  item.InvoiceType = "FISCALA_EMISA_IN_JG";
  item.Currency = item.MONEDA_FF;
  item.TotalPaid = item.INCASAT;
  item.TotalDue = item.REST_DE_INCASAT;
  item.TotalAmount = item.TOTAL;
  //https://github.com/VldMrgnn/jk-react-app/issues/120
  item.EFFX_STATUS = (item.EFFX_STATUS || "").trim();
  return item;
}

export function mapperIvf(db): Operation<(item: any) => any> {
  return resource(function* (provide) {
    const memoKey = yield* call(() => getInvoiceMapperMemoKey(db));
    //
    const idxRates = yield* call(() => readRatesIdxMemo(db, memoKey));
    const latestRate = yield* call(() => readLatestRateMemo(idxRates, memoKey));
    const issuersByAbrv = yield* call(() => getIssuersIdxMemo(db, memoKey));

    function* preparredFn(item): Operation<unknown> {
      const mpItem = yield* mapIvFiscalToMain(
        item,
        idxRates,
        latestRate,
        issuersByAbrv
      );
      return mpItem;
    }
    yield* provide(preparredFn);
  });
}
